import { Link } from "react-router-dom";
import { Phone, Stethoscope, Activity, HandHeart, MessageCircle, ShieldCheck, Heart, Users, ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { TrustBar } from "@/components/TrustBar";
import { ConsultationCTA } from "@/components/ConsultationCTA";
import { SERVICES } from "@/data/services";
import { AREAS } from "@/data/areas";
import { COMPANY } from "@/lib/constants";

const ICONS = { Stethoscope, Activity, HandHeart, MessageCircle, ShieldCheck, Heart, Users };

export default function Services() {
  useSEO({
    title: "Home Health Services in Northern Virginia | Esprit Home Healthcare",
    description: "Explore skilled nursing, physical/occupational/speech therapy, medical social work, personal care and private duty services delivered at home across Northern Virginia.",
    canonical: "/services",
  });

  return (
    <div>
      <PageHeader
        overline="Our Services"
        title="Complete Home Health Care"
        subtitle="Whatever it takes to provide the best possible care at home. Choose a service to learn more — Esprit is a preferred provider for most major insurance plans."
      />
      <TrustBar />

      <section className="container-page py-16 lg:py-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Stethoscope;
            return (
              <Reveal key={s.slug} delay={i * 0.06}>
                <Link to={`/services/${s.slug}`} data-testid={`service-link-${s.slug}`}
                  className="group flex flex-col h-full bg-card rounded-2xl border border-border overflow-hidden hover:border-primary hover:-translate-y-1 transition-all duration-300">
                  <div className="h-48 overflow-hidden">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/10 text-primary -mt-12 mb-3 relative bg-card border border-border">
                      <Icon className="w-6 h-6" />
                    </span>
                    <h2 className="font-heading font-bold text-2xl text-primary">{s.name}</h2>
                    <p className="text-muted-foreground mt-2 flex-1">{s.intro.length > 130 ? s.intro.slice(0, 130) + "…" : s.intro}</p>
                    <span className="inline-flex items-center gap-1 text-primary font-semibold mt-4 group-hover:gap-2 transition-all">
                      Learn More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>

        {/* Service areas internal links */}
        <Reveal>
          <div className="bg-[hsl(var(--muted))] rounded-[2rem] p-8 mt-14 text-center">
            <h2 className="font-heading font-bold text-2xl text-primary">Available Across Northern Virginia</h2>
            <p className="text-muted-foreground mt-2">We bring every service to your community.</p>
            <div className="flex flex-wrap justify-center gap-2 mt-5">
              {AREAS.map((a) => (
                <Link key={a.slug} to={`/service-areas/${a.slug}`} className="rounded-full bg-card border border-border hover:bg-primary hover:text-primary-foreground px-4 py-2 text-sm font-medium transition-colors">
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <ConsultationCTA title="Nobody makes it easier to start home care" />
    </div>
  );
}
